import React, { useState, useRef, useCallback, useEffect } from 'react';
import './style.scss';
import clsx from 'clsx';
import GenericPlaceholder from '../Shared/components/GenericPlaceholder/index.js';
import GlobalSearchComponent from '../Shared/components/SearchComponent';
import { withTranslation } from 'react-i18next';
import { SearchContext } from '../Shared/components/SearchComponent/context/SearchContext';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { helpers, getParam } from '../Shared/components/SearchComponent/helpers';
import SearchTitle from '../Shared/components/SearchComponent/SearchTitle';
import PopularSearch from '../Shared/components/SearchComponent/PopularSearch';
import { useLazyQuery, useQuery } from 'react-apollo';
import { loader as gqlLoader } from 'graphql.macro';
import { debounce } from '@mui/material';
import PaginationComponent from '../Shared/components/SearchComponent/Pagination';
import SearchCategories from '../Shared/components/SearchComponent/SearchCategories';
import ResultsItems from '../Shared/components/SearchComponent/ResultsItems';
import SearchResultInfo from '../Shared/components/SearchResultInfo';

const query = gqlLoader(
    '../Shared/components/SearchComponent/API/GlobalSearchSuggestionsQuery.graphql'
);
const queryResultsPage = gqlLoader(
    '../Shared/components/SearchComponent/API/GlobalSearcResultPage.graphql'
);

const SearchResults = (props) => {
    const { t, params, i18n } = props;
    const {
        enablePopularSearches,
        inputLabel,
        placeholder,
        popularSearchTitle,
        popularSearches,
        resultPage,
        title,
        pageSize,
    } = props?.fields;

    const [selectedCategory, setSelectedCategory] = useState(0);
    const [totalSearchResultsCount, setTotalSearchResultsCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentSearchCategory, setCurrentSearchCategory] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [wordsNotFoundError, setWordsNotFoundError] = useState(false);
    const [dataRetrievedItems, setDataRetrievedItems] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [dataRetrievedForCategory, setDataRetrievedForCategory] = useState([]);
    const [resultsPageDataRetrievedItems, setResultsPageDataRetrievedItems] = useState([]);
    const [currentCategory, setCurrentCategory] = useState(t('All'));
    const openInSamePage = true;

    const [pagination, setPagination] = useState({
        currentPage: 1,
        itemsPerPage: pageSize?.value,
        pagesTotalNumber: 1,
        hasNext: false,
        hasPrevious: false,
        itemsCount: 0,
    });

    const pageSizesArray = [
        {
            fields: {
                DropdownValue: { value: pageSize?.value },
                DropdownText: { value: pageSize?.value },
            },
        },
    ];
    const hasPagination = true;

    const inputSearchRef = useRef(null);
    let language = i18n.language;
    let facetOn = 'pagesection';
    const startSearchCharatersLength = pageSize?.value + 1;
    let templateName = '';
    const fromPopup = false;
    const context = useSitecoreContext();

    let indexName = 'FAB2_#datebase#_index'.replace(
        '#datebase#',
        context?.sitecoreContext?.route?.databaseName
    );

    let summaryText = 'resultsSummary';

    // Utility function to filter out hidden items
    const filterHiddenItems = (items) => {
        if (!items) return items;
        return items.filter(item => {
            const hideFromSearch = item?.item?.hidePage?.value;
            return hideFromSearch !== "1" && hideFromSearch !== "true";
        });
    };

    //*START*****Search Suggestion popup data********//
    const [getSearchSuggestionsData, { }] = useLazyQuery(
        query,
        {
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                if (searchTerm === '') {
                    return null;
                }
                setWordsNotFoundError(false);
                setIsOpen(false);

                let items = data?.search?.results?.items;
                items = filterHiddenItems(items);

                if (items?.length <= 0) {
                    setWordsNotFoundError(true);
                } else if (!!items && items?.length > 0) {
                    setDataRetrievedItems(items);
                    summaryText = items?.length > 0 ? 'resultsSummary' : 'resultSummary';
                    setIsOpen(items?.length > 0);
                    inputSearchRef.current.focus();
                }
            },
        },
        [searchTerm]
    );
    //*END*****Search Suggestion popup data********//

    //*START*****Search page results data********//
    const [getSearchResults, { }] = useLazyQuery(
        query,
        {
            fetchPolicy: 'network-only',
            onCompleted: (resultsData) => {
                settingStatesForData({ setCount: true }, resultsData);
            },
        },
        []
    );

    const [getSearchResultsWithoutSettingTotal, { }] = useLazyQuery(
        query,
        {
            fetchPolicy: 'network-only',
            onCompleted: (resultsData2) => {
                settingStatesForData({ setCount: false }, resultsData2);
            },
        },
        []
    );

    const [filterVariable, setFilterVariable] = useState({ index: `${indexName}`, first: '1' });
    const { } = useQuery(queryResultsPage, {
        variables: filterVariable,
        onCompleted: (resultsData1) => {
            settingStatesForData({ setCount: true, setCategory: true }, resultsData1);
        },
    });

    const settingStatesForData = (setup, data) => {
        let items = data?.search?.results?.items;
        items = filterHiddenItems(items);

        if (items?.length <= 0) {
            setResultsPageDataRetrievedItems([]);
            if (setup.setCategory) {
                setDataRetrievedForCategory([]);
            }
            if (setup.setCount) {
                setTotalSearchResultsCount(0);
            }
        } else if (!!items && items?.length > 0) {
            let totalCount = data?.search?.results?.totalCount;

            setResultsPageDataRetrievedItems(items);

            if (setup.setCount) {
                setTotalSearchResultsCount(totalCount);
            }
            if (setup.setCategory) {
                setDataRetrievedForCategory(data?.search?.facets[0]?.values);
            }

            let totalPagesNumber =
                totalCount / pageSize?.value < 1 ? 1 : Math.ceil(totalCount / pageSize?.value);
            setPagination((prev) => {
                return {
                    ...prev,
                    itemsCount: totalCount,
                    hasNext: pagination?.currentPage < totalPagesNumber,
                    hasPrevious: pagination?.currentPage > 1,
                    itemsPerPage: pageSize?.value,
                    pagesTotalNumber: totalPagesNumber,
                };
            });
        }
    };

    const getSearchSuggestions = useCallback(debounce(getSearchSuggestionsData, 500), []);

    const providerValue = {
        t,
        popularSearches,
        pagination,
        language,
        indexName,
        templateName,
        title,
        popularSearchTitle,
        popularSearches,
        pageSize,
        enablePopularSearches,
        inputLabel,
        placeholder,
        resultPage,
        searchTerm,
        setSearchTerm,
        startSearchCharatersLength,
        helpers,
        isOpen,
        setIsOpen,
        wordsNotFoundError,
        setWordsNotFoundError,
        dataRetrievedItems,
        setDataRetrievedItems,
        focusedIndex,
        setFocusedIndex,
        getSearchSuggestions,
        hasPagination,
        summaryText,
        fromPopup,
        inputSearchRef,
        resultsPageDataRetrievedItems,
        setPagination,
        dataRetrievedForCategory,
        totalSearchResultsCount,
        setTotalSearchResultsCount,
        facetOn,
        getSearchResults,
        filterVariable,
        setFilterVariable,
        openInSamePage,
        currentCategory,
        setCurrentCategory,
        currentSearchCategory,
        setCurrentSearchCategory,
        getSearchResultsWithoutSettingTotal,
        isCategoryDropdownOpen,
        setIsCategoryDropdownOpen,
        selectedCategory,
        setSelectedCategory,
    };

    useEffect(() => {
        let keyword = getParam();
        if (!!keyword && keyword !== '') {
            setSearchTerm(keyword);
            setCurrentSearchCategory(keyword);
        }
        helpers({
            indexName,
            templateName,
            language,
            pagination,
            keyword,
            hasPagination,
            facetOn,
            pageSize: pageSize?.value,
            setFilterVariable,
        });
    }, []);

    const shouldRenderCategories =
        Array.isArray(dataRetrievedForCategory) && dataRetrievedForCategory?.length > 0;
    if (!props?.fields || !props.params) {
        return <GenericPlaceholder componentName="SearchResults" />;
    }
    return (
        <div className={clsx('search-results-page', 'add-margins', params?.Styles)}>
            <div className="top-search-result">
                <div className="ellipse">
                    <img border="none" src={'/-/media/Project/FAB2/shared/images/ellispse_green.svg'} />
                </div>
                <SearchContext.Provider value={providerValue}>
                    <div className="content main-content">
                        <div className="popup-search-wrap">
                            <SearchTitle />
                            <GlobalSearchComponent />
                            <PopularSearch />
                        </div>

                        <div className="search-results-data flex-it">
                            <div className="search-results-left">
                                <SearchResultInfo />
                                {shouldRenderCategories && <SearchCategories />}
                            </div>
                            <div className="search-results-right">
                                {resultsPageDataRetrievedItems?.length > 0 && <ResultsItems />}
                                {hasPagination && resultsPageDataRetrievedItems?.length > 0 && (
                                    <PaginationComponent pageSizes={pageSizesArray} showItemsPerPage={false} t={t} />
                                )}
                            </div>
                        </div>
                    </div>
                </SearchContext.Provider>
            </div>
        </div>
    );
};

export default withTranslation()(SearchResults);